import {
  EventType,
  Fit,
  Layout,
  StateMachineInput,
  useRive,
  type Event,
} from '@rive-app/react-canvas'
import { AnimatePresence } from 'motion/react'
import { motion } from '~/lib/motion'
import { useEffect, useRef, useState } from 'react'
import rewardRiv from '~/assets/rive/reward.riv'
import { rewardFlow, type Item } from '~/features/reward-flow/store'
import { m } from '~/paraglide/messages'
import { Indicator } from './indicator'

export function MainContent({
  item,
  items,
  currentIndex,
}: {
  item: Item
  items: Item[]
  currentIndex: number
}) {
  const clickLockRef = useRef(false)
  const [isButtonVisible, setIsButtonVisible] = useState(false)
  useEffect(() => {
    const timer = setTimeout(
      () => {
        setIsButtonVisible(true)
      },
      item.originalItem ? 1000 : 500,
    )
    return () => {
      clearTimeout(timer)
    }
  }, [item])
  const { RiveComponent, rive } = useRive({
    src: rewardRiv,
    autoplay: true,
    artboard: 'reward_reveal',
    stateMachines: ['State Machine 1'],
    layout: new Layout({ fit: Fit.FitHeight }),
  })
  useEffect(() => {
    if (rive) {
      const inputs = rive.stateMachineInputs('State Machine 1')
      let statusInput!: StateMachineInput
      let shouldFallbackInput!: StateMachineInput
      for (const input of inputs) {
        if (input.name === 'status') {
          statusInput = input
        }
        if (input.name === 'repeat') {
          shouldFallbackInput = input
        }
      }
      const shouldFallback = !!item.originalItem
      shouldFallbackInput.value = shouldFallback
      statusInput.value = item.bgAnimType

      if (shouldFallback) {
        const handleStateChange = (event: Event) => {
          const [state] = event.data as string[]
          if (state.endsWith('_loop')) {
            setIsShowingActualItem(true)
          }
        }
        rive.on(EventType.StateChange, handleStateChange)
        return () => {
          rive.off(EventType.StateChange, handleStateChange)
        }
      }
    }
  }, [rive, item])

  const shouldPerformFallback = !!item.originalItem
  const [isShowingActualItem, setIsShowingActualItem] = useState(
    !shouldPerformFallback,
  )

  return (
    <motion.div
      key="main"
      className="flex flex-1 flex-col"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      {/* Main */}
      <div className="flex flex-1 flex-col justify-center gap-6 px-6">
        {/* Congrats title */}
        <h1 className="text-center text-xl font-semibold text-white">
          {m.claimed_title()}
        </h1>

        {/* Reward image */}
        <div className="relative mx-auto flex h-[40vh] w-full max-w-[40vh] items-center justify-center">
          <RiveComponent />
          <AnimatePresence initial={shouldPerformFallback}>
            {isShowingActualItem ? (
              <motion.img
                key="actual-item"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                src={item.icon}
                className="absolute inset-0 m-auto size-[50%]"
                alt=""
              />
            ) : (
              <motion.img
                key="original-item"
                initial={{ filter: 'brightness(1)' }}
                animate={{
                  filter: 'brightness(50)',
                  transition: { duration: 1.2, ease: [0.7, 0, 0.84, 0] },
                }}
                exit={{ opacity: 0 }}
                src={item.originalItem!.icon}
                className="absolute inset-0 m-auto size-[50%]"
                alt=""
              />
            )}
          </AnimatePresence>
        </div>

        <AnimatePresence initial={shouldPerformFallback} mode="wait">
          <motion.h2
            key={isShowingActualItem ? item.title : item.originalItem?.title}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="text-center text-[30px] font-bold text-white"
            style={{
              textShadow: '0 0 15px rgba(255, 255, 255, 0.70)',
            }}
          >
            {isShowingActualItem ? item.title : item.originalItem?.title}
          </motion.h2>
        </AnimatePresence>

        {item.originalItem && (
          <p key="fallback-hint" className="text-center text-white">
            {m.duplicate_item_reclaimed()}
          </p>
        )}

        <Indicator items={items} currentIndex={currentIndex} />
      </div>
      {/* Footer */}
      <div className="mx-auto w-full max-w-[640px] shrink-0 px-6 pb-12">
        <div className="h-[52px]">
          <AnimatePresence>
            {isButtonVisible && (
              <motion.button
                className="h-[52px] w-full rounded-xl bg-white font-semibold text-[#176361]"
                onClick={() => {
                  if (clickLockRef.current) return
                  clickLockRef.current = true
                  rewardFlow.next()
                }}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
              >
                {m.cart_claim_active()}
              </motion.button>
            )}
          </AnimatePresence>
        </div>
      </div>
    </motion.div>
  )
}
